import { useState } from 'react';

import { isSameDay } from 'date-fns';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AnimatedCollapsibleContent,
  Collapsible,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import InfiniteScroll from '@/components/ui/infinite-scroll';
import { Separator } from '@/components/ui/separator';
import { formatHoursToTimeString, minutesToHours } from '@/helpers';
import { useModal } from '@/hooks/use-modal';
import { usePermissions } from '@/modules/common';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import {
  formatWallClockTime,
  formatWallClockWeekdayDate,
  getBrowserTimezone,
} from '@/utils/date-formatters/date-formatters';
import { logger } from '@/utils/logger/logger';

import { groupTracksByDate } from '../helpers/group-tracks-by-date.helper';
import { useStartTimer } from '../hooks/use-start-timer';
import {
  useCreateWorklog,
  useDeleteWorklog,
  useUpdateWorklog,
} from '../hooks/use-time-track-mutations';
import { useTimeTracks } from '../hooks/use-time-tracks';
import { useTimerControls } from '../hooks/use-timer-controls';
import { useViewModeAndDate } from '../hooks/use-view-mode-and-date';
import { useTimeTrackingStore } from '../store/time-tracking.store';
import { CreateWorklogRequest, UpdateWorklogRequest } from '../types/dto/worklogs-summary.dto';
import {
  EditableTimeEntry,
  ProjectTimerControls,
  ProjectTimerState,
  ProjectTimeTrackingSummary,
  TimeTrack,
  ViewMode,
} from '../types/time-tracking';
import { DeleteTimeEntryModal } from './modals/delete-time-entry-modal';
import { TimeEntryModal } from './modals/time-entry-modal';
import { ProjectHeader } from './project-header';
import { TimeTrackEntry } from './time-track-entry';

const TAG = 'ProjectCard:';

interface ProjectCardProps {
  project: ProjectTimeTrackingSummary;
}

interface WeekRowProps {
  isEditVisible: boolean;
  isDeleteVisible: boolean;
  date: string;
  tracks: TimeTrack[];
  bookedHoursPerDay: number;
  onEdit: (e: React.MouseEvent<HTMLButtonElement>, track: TimeTrack) => void;
  onDelete: (e: React.MouseEvent<HTMLButtonElement>, track: TimeTrack) => void;
}

function WeekRow({
  isEditVisible,
  isDeleteVisible,
  date,
  tracks,
  bookedHoursPerDay,
  onEdit,
  onDelete,
}: WeekRowProps) {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation('authenticated.timeTracking');

  const dailyTrackedHours = minutesToHours(
    tracks.reduce((sum, track) => sum + track.trackedMinutes, 0)
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="w-full">
      <div>
        <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md p-4 hover:bg-muted/50">
          <ChevronDown className={`h-4 w-4 transition-transform ${!open ? '-rotate-90' : ''}`} />
          <span className="text-sm font-medium">{formatWallClockWeekdayDate(date)}</span>
          <span className="ml-auto text-sm text-muted-foreground">
            {t('project.dailySummary', {
              tracked: formatHoursToTimeString(dailyTrackedHours),
              booked: formatHoursToTimeString(bookedHoursPerDay),
            })}
          </span>
        </CollapsibleTrigger>
        <AnimatedCollapsibleContent className="pl-2">
          {tracks.map(track => (
            <TimeTrackEntry
              key={track.id}
              track={track}
              onEdit={e => onEdit(e, track)}
              onDelete={e => onDelete(e, track)}
              isEditVisible={isEditVisible}
              isDeleteVisible={isDeleteVisible}
            />
          ))}
        </AnimatedCollapsibleContent>
      </div>
    </Collapsible>
  );
}

export function ProjectCard({ project }: ProjectCardProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  const { displayToastErrorFromApiWithFallback } = useApiError();
  const { viewMode, selectedDate } = useViewModeAndDate();
  const expandedProjects = useTimeTrackingStore(state => state.expandedProjects);
  const toggleExpanded = useTimeTrackingStore(state => state.toggleProjectExpanded);

  const setActiveTimerProject = useTimeTrackingStore(state => state.setActiveTimerProject);

  const isExpanded = expandedProjects[project.project.projectId] || false;

  const {
    activeTimer,
    activeTimerFetchedAt,
    isPaused,
    isLimitReached,
    isStopModalOpen,
    openStopModal,
    closeStopModal,
    timeTrack: activeTimerEntry,
    isDiscardModalOpen,
    openDiscardModal,
    closeDiscardModal,
    handleTogglePause,
    handleStopSubmit,
    handleSaveEntry,
    handleDiscard,
    isTogglePausePending,
    isStopPending,
    isDiscardPending,
    isAnyActionPending,
  } = useTimerControls();

  const {
    mutateAsync: startTimer,
    isPending: isStartPending,
    variables: startVariables,
  } = useStartTimer();
  const { mutateAsync: createWorklog } = useCreateWorklog();
  const { mutate: deleteWorklog, isPending: isDeleting } = useDeleteWorklog();
  const { mutateAsync: updateWorklog } = useUpdateWorklog();

  const {
    open: isWorklogModalOpen,
    onOpen: openWorklogModal,
    onClose: closeWorklogModal,
  } = useModal();
  const {
    open: isDeleteWorklogModalOpen,
    onOpen: openDeleteWorklogModal,
    onClose: closeDeleteWorklogModal,
  } = useModal();

  const [editingWorklog, setEditingWorklog] = useState<EditableTimeEntry | null>(null);

  const {
    data: timeTracksData,
    isLoading: isLoadingTimeTracks,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useTimeTracks({
    date: selectedDate,
    viewMode,
    projectId: project.project.projectId,
    enabled: isExpanded,
  });

  const timeTracks = timeTracksData?.items ?? [];

  const tracksByDate =
    viewMode === ViewMode.Week || viewMode === ViewMode.Month
      ? groupTracksByDate(timeTracks)
      : null;

  const { hasPermission } = usePermissions();
  const canEditWorklog = hasPermission(OpsiumPolicy.WorklogsEdit);
  const canDeleteWorklog = hasPermission(OpsiumPolicy.WorklogsDelete);
  const canCreateWorklog = hasPermission(OpsiumPolicy.WorklogsCreate);

  const isToday = isSameDay(selectedDate, new Date());
  const isTimerRunningHere = !!activeTimer && activeTimer.projectId === project.project.projectId;
  // Only show the start spinner on the card whose play button was clicked —
  // when global timer state is still null other cards must not light up.
  const isStartPendingHere =
    isStartPending && startVariables?.projectId === project.project.projectId;

  const defaultProject: DropdownSearchItem = {
    id: String(project.project.projectId),
    name: project.project.projectName,
    colorHex: project.project.colorHex,
    avatarLink: project.project.logoUrl ?? undefined,
  };

  const timerControls = resolveTimerControls({
    isToday,
    isTimerRunningHere,
    isTimerRunning: !!activeTimer,
    isPaused,
    isLimitReached,
    isStartPending: isStartPendingHere,
    isTogglePausePending,
    isStopPending,
    isDiscardPending,
    isAnyActionPending: isAnyActionPending || isStartPending,
    onPlay: async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (isStartPending || isAnyActionPending) return;
      try {
        await startTimer({
          projectId: project.project.projectId,
          description: null,
          timezone: getBrowserTimezone(),
        });
        // Persist the project so the sidebar timer widget can render it without
        // re-fetching full project details (which 403s for worklog-only users).
        setActiveTimerProject({
          id: String(project.project.projectId),
          name: project.project.projectName,
          colorHex: project.project.colorHex,
          avatarLink: project.project.logoUrl || undefined,
        });
      } catch (error) {
        logger.error(TAG, 'Failed to start timer for project', project.project.projectId, error);
        displayToastErrorFromApiWithFallback(
          error,
          'authenticated.timeTracking:api.error.activeTimerStartFailed'
        );
      }
    },
    onTogglePause: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      handleTogglePause();
    },
    onStop: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      openStopModal();
    },
    onReset: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      openDiscardModal();
    },
  });

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    openWorklogModal();
  };

  const handleEdit = (e: React.MouseEvent<HTMLButtonElement>, track: TimeTrack) => {
    e.preventDefault();
    setEditingWorklog({
      ...track,
      startTime: formatWallClockTime(track.date),
      project: {
        projectId: project.project.projectId,
        projectName: project.project.projectName,
        logoUrl: project.project.logoUrl,
        colorHex: project.project.colorHex,
      },
    });
    openWorklogModal();
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>, track: TimeTrack) => {
    e.preventDefault();
    setEditingWorklog(track);
    openDeleteWorklogModal();
  };

  const handleWorklogSubmit = async (values: CreateWorklogRequest) => {
    if (editingWorklog) {
      await updateWorklog({ ...values, id: editingWorklog.id } as UpdateWorklogRequest);
    } else {
      await createWorklog(values);
    }
  };

  const handleWorklogModalClose = () => {
    closeWorklogModal();
    setEditingWorklog(null);
  };

  const handleDeleteWorklogModalClose = () => {
    closeDeleteWorklogModal();
    setEditingWorklog(null);
  };

  return (
    <>
      <TimeEntryModal
        open={isWorklogModalOpen}
        onClose={handleWorklogModalClose}
        defaultProject={defaultProject}
        onSubmit={handleWorklogSubmit}
        timeTrack={editingWorklog ?? undefined}
      />

      {editingWorklog && (
        <DeleteTimeEntryModal
          open={isDeleteWorklogModalOpen}
          onOpen={openDeleteWorklogModal}
          onClose={handleDeleteWorklogModalClose}
          isLoading={isDeleting}
          onConfirm={() => {
            deleteWorklog(editingWorklog.id, { onSuccess: handleDeleteWorklogModalClose });
          }}
        />
      )}

      {isStopModalOpen && (
        <TimeEntryModal
          open={isStopModalOpen}
          onClose={closeStopModal}
          onSubmit={handleStopSubmit}
          timeTrack={activeTimerEntry}
          defaultProject={defaultProject}
          isRunning
          onSave={handleSaveEntry}
          activeTimer={activeTimer}
          activeTimerFetchedAt={activeTimerFetchedAt}
        />
      )}

      <ConfirmationModal
        open={isDiscardModalOpen}
        onClose={closeDiscardModal}
        title={t('tracker.cancelTracking.title')}
        message={t('tracker.cancelTracking.message')}
        confirmText={t('tracker.cancelTracking.confirm')}
        onConfirm={handleDiscard}
      />

      <Collapsible
        open={isExpanded}
        onOpenChange={() => toggleExpanded(project.project.projectId)}
        className="rounded-md border bg-card shadow-sm"
      >
        <CollapsibleTrigger className="w-full rounded-md hover:bg-muted/30">
          <div className="flex min-w-0 items-center gap-0">
            <div className="px-4 py-2">
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
              />
            </div>
            <ProjectHeader
              project={project}
              onAdd={handleAdd}
              isCreateWorklogActionVisible={canCreateWorklog}
              timerControls={timerControls}
            />
          </div>
        </CollapsibleTrigger>

        {isExpanded && <Separator className="mx-auto w-[calc(100%-32px)]" />}

        <AnimatedCollapsibleContent>
          {isLoadingTimeTracks && (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}

          {viewMode === ViewMode.Day && (
            <div className="p-2">
              <InfiniteScroll
                isLoading={isFetchingNextPage}
                hasMore={!!hasNextPage}
                next={fetchNextPage}
              >
                {timeTracks.map(track => (
                  <TimeTrackEntry
                    key={track.id}
                    track={track}
                    onEdit={e => handleEdit(e, track)}
                    onDelete={e => handleDelete(e, track)}
                    isEditVisible={canEditWorklog}
                    isDeleteVisible={canDeleteWorklog}
                  />
                ))}
              </InfiniteScroll>
            </div>
          )}

          {tracksByDate && (
            <InfiniteScroll
              isLoading={isFetchingNextPage}
              hasMore={!!hasNextPage}
              next={fetchNextPage}
            >
              {Object.entries(tracksByDate)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([date, tracks]) => (
                  <WeekRow
                    key={date}
                    date={date}
                    tracks={tracks}
                    bookedHoursPerDay={project.bookedHours}
                    isEditVisible={canEditWorklog}
                    isDeleteVisible={canDeleteWorklog}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
            </InfiniteScroll>
          )}

          {isFetchingNextPage && (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}

          {!isLoadingTimeTracks && timeTracks.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {t('project.noEntries')}
            </div>
          )}
        </AnimatedCollapsibleContent>
      </Collapsible>
    </>
  );
}

interface ResolveTimerControlsParams {
  isToday: boolean;
  isTimerRunningHere: boolean;
  isTimerRunning: boolean;
  isPaused: boolean;
  isLimitReached: boolean;
  isStartPending: boolean;
  isTogglePausePending: boolean;
  isStopPending: boolean;
  isDiscardPending: boolean;
  isAnyActionPending: boolean;
  onPlay: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onTogglePause: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onStop: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onReset: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

function resolveTimerControls({
  isToday,
  isTimerRunningHere,
  isTimerRunning,
  isPaused,
  isLimitReached,
  isStartPending,
  isTogglePausePending,
  isStopPending,
  isDiscardPending,
  isAnyActionPending,
  onPlay,
  onTogglePause,
  onStop,
  onReset,
}: ResolveTimerControlsParams): ProjectTimerControls {
  if (!isToday) {
    return { state: ProjectTimerState.Unavailable };
  }
  if (isTimerRunningHere) {
    return {
      state: ProjectTimerState.ActiveHere,
      isPaused,
      isLimitReached,
      isTogglePausePending,
      isStopPending,
      isDiscardPending,
      isAnyActionPending,
      onTogglePause,
      onStop,
      onReset,
    };
  }
  if (isTimerRunning) {
    return { state: ProjectTimerState.ActiveElsewhere };
  }
  return {
    state: ProjectTimerState.Idle,
    onPlay,
    isStartPending,
    isAnyActionPending,
  };
}
